import { Alert, Anchor, Badge, Box, Button, Drawer, Group, Loader, Select, Stack, Switch, Table, Text, TextInput, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconExternalLink, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { dayjs } from '../format';
import { useSession } from '../session';

interface Source {
  id: string;
  code: string;
  kind: 'dds' | 'rnp' | 'em' | 'plan';
  legal_entity: string | null;
  alias_source: string | null;
  spreadsheet_id: string;
  data_sheet: string;
  reference_sheet: string | null;
  is_active: boolean;
  last_synced_at: string | null;
  last_error: string | null;
}
interface Problem { source_code: string | null; problem: string; detail: string; since: string }
interface Run { id: string; source: string; file_name: string | null; started_at: string; status: 'running' | 'succeeded' | 'failed'; errors: number; warnings: number }
interface Issue { id: string; severity: 'error' | 'warning' | 'info'; code: string; row_ref: string | null; message: string }

const KINDS: Record<Source['kind'], string> = { dds: 'ДДС', rnp: 'РНП, журнал', em: 'ЭМ, маркетинг', plan: 'РНП, лист «План»' };
const STATUS: Record<Run['status'], [string, string]> = { running: ['идёт', 'blue'], succeeded: ['загружено', 'brand'], failed: ['ошибка', 'red'] };
const when = (t: string | null) => (t ? dayjs(t).format('D MMM, HH:mm') : 'ни разу');

/** Источники Google и загрузки: что подключено, когда загружалось, что пошло не так. */
export function SourcesPage() {
  const mobile = useMediaQuery('(max-width: 48em)');
  const [data, setData] = useState<{ can_edit: boolean; sources: Source[]; problems: Problem[]; runs: Run[] } | null>(null);
  const [run, setRun] = useState<Run | null>(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(async () => setData(await api.get('/sources')), []);
  useEffect(() => {
    load().catch((e) => notifications.show({ color: 'red', message: e.message }));
  }, [load]);

  if (!data) {
    return (
      <Group justify="center" p="xl">
        <Loader />
      </Group>
    );
  }
  const toggle = async (s: Source, v: boolean) => {
    try {
      await api.patch(`/sources/${s.id}`, { is_active: v });
      await load();
    } catch (e) {
      notifications.show({ color: 'red', message: (e as Error).message });
    }
  };

  return (
    <Box p={{ base: 'md', sm: 'xl' }} maw={1100}>
      <Group justify="space-between" mb="md">
        <Title order={2}>Источники</Title>
        {data.can_edit && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => setAdding(true)}>
            Подключить таблицу
          </Button>
        )}
      </Group>
      <Text size="sm" c="dimmed" mb="md" maw={760}>
        Google Таблицы, из которых МИС забирает данные по расписанию (каждые 15 минут на сервере). Правят данные в самих таблицах; здесь видно, загрузились ли они.
      </Text>

      {data.problems.length > 0 && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />} mb="md" title="Требует внимания">
          <Stack gap={2}>
            {data.problems.map((p, i) => (
              <Text key={i} size="sm">
                <b>{p.source_code ?? 'клиника'}</b>: {p.detail}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      <Stack gap="sm" mb="xl">
        {data.sources.length === 0 && <Text c="dimmed">Источников пока нет</Text>}
        {data.sources.map((s) => (
          <Group key={s.id} className="mis-card" p="md" justify="space-between" wrap="wrap" style={{ opacity: s.is_active ? 1 : 0.6 }}>
            <div>
              <Group gap={8}>
                <Text fw={600}>{s.code}</Text>
                <Badge variant="default" tt="none" fw={500}>{KINDS[s.kind]}</Badge>
                {s.legal_entity && <Badge variant="light" tt="none">{s.legal_entity}</Badge>}
              </Group>
              <Text size="sm" c="dimmed">
                Лист «{s.data_sheet}»{s.reference_sheet ? `, справочник «${s.reference_sheet}»` : ''} · последняя загрузка: {when(s.last_synced_at)}
              </Text>
              {s.last_error && <Text size="sm" c="red.7">{s.last_error}</Text>}
            </div>
            <Group gap="md">
              <Anchor href={`https://docs.google.com/spreadsheets/d/${s.spreadsheet_id}`} target="_blank" rel="noreferrer" size="sm">
                <Group gap={4}>Таблица <IconExternalLink size={14} /></Group>
              </Anchor>
              <Switch label="Загружать" checked={s.is_active} disabled={!data.can_edit} onChange={(e) => void toggle(s, e.currentTarget.checked)} />
            </Group>
          </Group>
        ))}
      </Stack>

      <Title order={4} mb="sm">Последние загрузки</Title>
      <Box className="mis-card" style={{ overflow: 'hidden' }}>
        {data.runs.length === 0 ? (
          <Text c="dimmed" p="xl" ta="center">Загрузок ещё не было</Text>
        ) : (
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Когда</Table.Th>
                <Table.Th>Источник</Table.Th>
                {!mobile && <Table.Th>Файл</Table.Th>}
                <Table.Th>Итог</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.runs.map((r) => (
                <Table.Tr key={r.id} className="mis-row" onClick={() => setRun(r)}>
                  <Table.Td c="dimmed" style={{ whiteSpace: 'nowrap' }}>{when(r.started_at)}</Table.Td>
                  <Table.Td>{r.source}</Table.Td>
                  {!mobile && <Table.Td c="dimmed">{r.file_name ?? '—'}</Table.Td>}
                  <Table.Td>
                    <Group gap={6}>
                      <Badge color={STATUS[r.status][1]} variant="light">{STATUS[r.status][0]}</Badge>
                      {r.errors > 0 && <Badge color="red" variant="outline">ошибок: {r.errors}</Badge>}
                      {r.warnings > 0 && <Badge color="orange" variant="outline">предупреждений: {r.warnings}</Badge>}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Box>

      <Drawer opened={run !== null} onClose={() => setRun(null)} position={mobile ? 'bottom' : 'right'} size={mobile ? '92%' : 520}
        title={<Text fw={600}>{run ? `${run.source} · ${when(run.started_at)}` : ''}</Text>}>
        {run && <RunIssues run={run} />}
      </Drawer>
      <Drawer opened={adding} onClose={() => setAdding(false)} position={mobile ? 'bottom' : 'right'} size={mobile ? '92%' : 460}
        title={<Text fw={600}>Подключить таблицу</Text>}>
        {adding && <AddSource done={() => { setAdding(false); void load(); }} />}
      </Drawer>
    </Box>
  );
}

function RunIssues({ run }: { run: Run }) {
  const [issues, setIssues] = useState<Issue[] | null>(null);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    api
      .get<{ issues: Issue[]; hidden?: boolean }>(`/sources/runs/${run.id}/issues`)
      .then((r) => {
        setIssues(r.issues);
        setHidden(!!r.hidden);
      })
      .catch((e) => notifications.show({ color: 'red', message: e.message }));
  }, [run.id]);
  if (!issues) return <Loader />;
  if (hidden) {
    return (
      <Text c="dimmed">
        Ошибок: {run.errors}, предупреждений: {run.warnings}. Текст замечаний (в нём суммы и статьи) видят собственник и финансист.
      </Text>
    );
  }
  if (!issues.length) return <Text c="dimmed">Загрузка прошла без замечаний.</Text>;
  return (
    <Stack gap="xs">
      <Text size="sm" c="dimmed">Исправьте строки в таблице — следующая загрузка подхватит правку.</Text>
      {issues.map((i) => (
        <Box key={i.id} className="mis-card" px="md" py="sm" style={{ borderRadius: 12 }}>
          <Group gap={6} mb={2}>
            <Badge size="sm" color={i.severity === 'error' ? 'red' : i.severity === 'warning' ? 'orange' : 'gray'} variant="light">
              {i.severity === 'error' ? 'ошибка' : i.severity === 'warning' ? 'предупреждение' : 'сведения'}
            </Badge>
            {i.row_ref && <Text size="xs" c="dimmed">{i.row_ref}</Text>}
          </Group>
          <Text size="sm">{i.message}</Text>
        </Box>
      ))}
    </Stack>
  );
}

function AddSource({ done }: { done: () => void }) {
  const { catalog } = useSession();
  const [f, setF] = useState({ code: '', kind: 'dds', legal_entity_id: null as string | null, alias_source: '', spreadsheet: '', data_sheet: '', reference_sheet: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f, v: string | null) => setF((x) => ({ ...x, [k]: v }));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/sources', { ...f, alias_source: f.alias_source || null, reference_sheet: f.reference_sheet || null });
      notifications.show({ color: 'brand', message: 'Источник подключён: проверьте первую загрузку' });
      done();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="md">
      {error && <Alert color="red" variant="light" role="alert">{error}</Alert>}
      <Alert color="gray" variant="light">
        Сначала откройте доступ «Читатель» к таблице для сервисного аккаунта Google (docs/google-sheets.md). Первая загрузка — на сервере пробным прогоном:{' '}
        <code>node sync/sheets-sync.mjs --dry-run --source код</code>.
      </Alert>
      <TextInput label="Адрес таблицы" placeholder="https://docs.google.com/spreadsheets/d/…" value={f.spreadsheet} onChange={(e) => set('spreadsheet', e.currentTarget.value)} />
      <Select label="Что в таблице" data={Object.entries(KINDS).map(([value, label]) => ({ value, label }))} value={f.kind} onChange={(v) => set('kind', v)} allowDeselect={false} />
      <Select label="ООО" placeholder="Из колонки «ООО» в таблице" clearable data={(catalog?.legalEntities ?? []).map((l) => ({ value: l.id, label: l.name }))} value={f.legal_entity_id} onChange={(v) => set('legal_entity_id', v)} />
      <TextInput label="Код источника" description="латиницей: dds_lady, costs_sales" value={f.code} onChange={(e) => set('code', e.currentTarget.value)} />
      <TextInput label="Лист с данными" value={f.data_sheet} onChange={(e) => set('data_sheet', e.currentTarget.value)} />
      <TextInput label="Лист-справочник (если есть)" value={f.reference_sheet} onChange={(e) => set('reference_sheet', e.currentTarget.value)} />
      <TextInput label="Набор соответствий" description="код источника, чьи соответствия статей использовать; пусто — свой" value={f.alias_source} onChange={(e) => set('alias_source', e.currentTarget.value)} />
      <Button onClick={() => void submit()} loading={busy} disabled={!f.spreadsheet || !f.code || !f.data_sheet}>
        Подключить
      </Button>
    </Stack>
  );
}
